import { KeyRoundIcon, MailIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import useSWR, { mutate as globalMutate } from 'swr'

import { AuthShell } from '../components/AuthShell'
import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceLink,
  PreferenceScreen,
  PreferenceSubmit,
} from '../components/Preference'
import {
  ME_KEY,
  promptGoogleOneTap,
  signInWithGoogle,
  useConfig,
  useMe,
} from '../lib/auth-client'

type InvitePreview = {
  tenantId: number
  tenantName: string
  following?: boolean
}

async function inviteFetcher(url: string): Promise<InvitePreview> {
  const res = await fetch(url, { credentials: 'include' })
  const data = await res.json()
  if (!res.ok) throw new Error(data.message || 'Ongeldig')
  return data
}

/** Invite: follow the household, or join it. */
export default function Invite() {
  const { code = '' } = useParams()
  const navigate = useNavigate()
  const { data: config } = useConfig()
  const googleEnabled = config?.googleEnabled ?? false
  const invite = useSWR(
    code ? `/api/invites/code/${encodeURIComponent(code)}` : null,
    inviteFetcher
  )
  const { data: me, mutate: mutateMe } = useMe()
  const [busy, setBusy] = useState<'follow' | 'join' | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [needsAuth, setNeedsAuth] = useState(false)

  const callbackURL = `/invite/${encodeURIComponent(code)}`
  const info = invite.data ?? null
  const error = actionError || invite.error?.message || null

  const currentTenant = me?.tenant ?? null
  const alreadyMember =
    currentTenant != null && info != null && currentTenant.id === info.tenantId
  const hasHousehold = currentTenant != null
  const switching =
    currentTenant != null && info != null && currentTenant.id !== info.tenantId
  const signedIn = me?.user != null && !me.user.isAnonymous && !needsAuth
  const alreadyFollowing = Boolean(info?.following)

  useEffect(() => {
    if (!me) return
    if (!me.user || me.user.isAnonymous) {
      setNeedsAuth(true)
      if (!me.user) void promptGoogleOneTap(callbackURL)
    }
  }, [me, callbackURL])

  async function postInvite(action: 'follow' | 'join') {
    setBusy(action)
    setActionError(null)
    try {
      const res = await fetch(`/api/invites/code/${encodeURIComponent(code)}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      })
      const data = await res.json()
      if (!res.ok) {
        if (res.status === 401 || data.message?.includes('e-mail')) {
          setNeedsAuth(true)
        }
        setActionError(data.message || 'Mislukt')
        return
      }
      await mutateMe()
      if (action === 'join') {
        await globalMutate((key) => key !== ME_KEY, undefined, {
          revalidate: false,
        })
        navigate('/planning', { replace: true })
        return
      }
      await invite.mutate()
      navigate('/planning', { replace: true })
    } catch (e) {
      setActionError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const nextParam = encodeURIComponent(callbackURL)
  const canAct = signedIn && info && !alreadyMember
  // Prefer follow when the user already belongs somewhere.
  const preferFollow = hasHousehold

  return (
    <AuthShell>
      <PreferenceScreen title="Uitnodiging" backHref="/">
        <PreferenceCategory filled>
          {info ? (
            <PreferenceInfo
              title={info.tenantName}
              summary={
                alreadyMember
                  ? 'Je bent al lid van dit huishouden.'
                  : alreadyFollowing
                    ? 'Je volgt dit huishouden al. Je kunt ook lid worden.'
                    : hasHousehold
                      ? 'Volg hun kookboek, of word lid van hun huishouden.'
                      : 'Je bent uitgenodigd. Volg hen of word lid.'
              }
            />
          ) : invite.isLoading ? (
            <PreferenceInfo title="Laden…" />
          ) : null}
          {switching ? (
            <PreferenceInfo
              title={`Nu: ${currentTenant.name}`}
              summary="Deelnemen verplaatst je naar hun huishouden."
            />
          ) : null}
          {error ? (
            <PreferenceInfo title={error} className="text-red-300" />
          ) : null}
          {alreadyMember ? (
            <PreferenceSubmit
              title="Naar kookboek"
              onPress={() => navigate('/recipes', { replace: true })}
            />
          ) : null}
        </PreferenceCategory>

        {canAct && preferFollow ? (
          <>
            <PreferenceCategory filled title="Aanbevolen">
              <PreferenceSubmit
                title={
                  busy === 'follow'
                    ? 'Bezig…'
                    : alreadyFollowing
                      ? 'Al aan het volgen'
                      : 'Volgen'
                }
                onPress={() => void postInvite('follow')}
                disabled={busy != null || alreadyFollowing}
              />
            </PreferenceCategory>
            <PreferenceCategory filled title="Of">
              <PreferenceButton
                title={
                  busy === 'join'
                    ? 'Bezig…'
                    : switching
                      ? 'Overschakelen naar dit huishouden'
                      : 'Deelnemen'
                }
                summary={
                  switching
                    ? 'Je verlaat je huidige huishouden'
                    : 'Word lid van dit huishouden'
                }
                onPress={() => void postInvite('join')}
                disabled={busy != null}
                isDestructive={switching}
              />
            </PreferenceCategory>
          </>
        ) : null}

        {canAct && !preferFollow ? (
          <>
            <PreferenceCategory filled title="Aanbevolen">
              <PreferenceSubmit
                title={busy === 'join' ? 'Bezig…' : 'Deelnemen'}
                onPress={() => void postInvite('join')}
                disabled={busy != null}
              />
            </PreferenceCategory>
            <PreferenceCategory filled title="Of">
              <PreferenceButton
                title={
                  busy === 'follow'
                    ? 'Bezig…'
                    : alreadyFollowing
                      ? 'Al aan het volgen'
                      : 'Alleen volgen'
                }
                summary="Zie hun lijsten zonder lid te worden"
                onPress={() => void postInvite('follow')}
                disabled={busy != null || alreadyFollowing}
              />
            </PreferenceCategory>
          </>
        ) : null}

        {needsAuth ? (
          <PreferenceCategory filled title="Account verplicht">
            {googleEnabled ? (
              <PreferenceButton
                icon={<GoogleIcon className="h-6 w-6" />}
                title="Inloggen met Google"
                onPress={() => void signInWithGoogle(callbackURL)}
                disabled={busy != null}
              />
            ) : null}
            <PreferenceLink
              icon={<MailIcon className="h-6 w-6" />}
              title="Inloggen met e-mailadres"
              summary="Ontvang een inloglink"
              href={`/login/email?next=${nextParam}`}
            />
            <PreferenceLink
              icon={<KeyRoundIcon className="h-6 w-6" />}
              title="Inloggen met wachtwoord"
              href={`/login?next=${nextParam}`}
            />
          </PreferenceCategory>
        ) : null}
      </PreferenceScreen>
    </AuthShell>
  )
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <title>Google</title>
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
    </svg>
  )
}
